import { useCallback, useEffect, useState, type Ref } from "react"
import type { DockviewApi } from "dockview"
import { LocalSelectionTransfer, PanelTransfer } from "dockview-core/dist/esm/dnd/dataTransfer"

const OVERFLOW_CONTAINER_SELECTOR = ".dv-tabs-overflow-container"
const OVERFLOW_DROPDOWN_ROOT_SELECTOR = ".dv-tabs-overflow-dropdown-root"

export function closeOverflowPopover(): void {
  const isOpen = document.querySelector(OVERFLOW_CONTAINER_SELECTOR)
  if (!isOpen) return
  document.body.dispatchEvent(
    new PointerEvent("pointerdown", { bubbles: true, cancelable: true, clientX: 0, clientY: 0 }),
  )
}

function clearPanelTransferState(
  transfer: ReturnType<typeof LocalSelectionTransfer.getInstance>,
): void {
  transfer.clearData(PanelTransfer.prototype)
}

function setOverflowDragTransfer(
  transfer: ReturnType<typeof LocalSelectionTransfer.getInstance>,
  dockviewId: string,
  groupId: string,
  panelId: string,
): void {
  // Always clear before setData so interrupted drags cannot leave stale PanelTransfer data.
  clearPanelTransferState(transfer)
  transfer.setData(
    [new PanelTransfer(dockviewId, groupId, panelId)],
    PanelTransfer.prototype,
  )
}

function attachTabDragGhost(tabElement: HTMLElement, dataTransfer: DataTransfer): void {
  const ghost = tabElement.cloneNode(true) as HTMLElement
  ghost.style.position = "absolute"
  ghost.style.top = "-9999px"
  document.body.appendChild(ghost)
  dataTransfer.setDragImage(ghost, 0, 0)
  requestAnimationFrame(() => ghost.remove())
}

export function attachOverflowPanelDrag(container: HTMLElement, api: DockviewApi): void {
  const tabs = container.querySelectorAll<HTMLElement>(".dv-tab")
  const transfer = LocalSelectionTransfer.getInstance()
  tabs.forEach((el) => {
    if (el.hasAttribute("data-op-overflow-drag")) return
    if (!el.querySelector("[data-panel-id]")) return
    el.setAttribute("data-op-overflow-drag", "true")
    el.draggable = true
    el.addEventListener("dragstart", (e: DragEvent) => {
      clearPanelTransferState(transfer)
      if (!e.dataTransfer) return
      const tab = e.currentTarget
      if (!(tab instanceof HTMLElement)) return
      const panelId = tab.querySelector("[data-panel-id]")?.getAttribute("data-panel-id")
      if (!panelId) return
      const panel = api.getPanel(panelId)
      if (!panel?.group) return
      setOverflowDragTransfer(transfer, api.id, panel.group.id, panelId)
      e.dataTransfer.effectAllowed = "move"
      e.dataTransfer.setData("text/plain", "")
      attachTabDragGhost(tab, e.dataTransfer)
    })
    el.addEventListener("dragend", () => {
      clearPanelTransferState(transfer)
      closeOverflowPopover()
    })
  })
}

function subscribeOverflowPanelDrag(api: DockviewApi, dockviewHost: HTMLElement): () => void {
  const tryAttach = () => {
    const container = dockviewHost.querySelector<HTMLElement>(OVERFLOW_CONTAINER_SELECTOR)
    if (container) attachOverflowPanelDrag(container, api)
  }

  const transfer = LocalSelectionTransfer.getInstance()

  const onDrop = () => {
    queueMicrotask(() => {
      // After sync drop handlers (dockview) read LocalSelectionTransfer, clear singleton state.
      clearPanelTransferState(transfer)
    })
  }

  const onClick = (event: MouseEvent) => {
    const target = event.target
    if (!(target instanceof HTMLElement) || !target.closest(OVERFLOW_DROPDOWN_ROOT_SELECTOR)) return
    tryAttach()
  }

  dockviewHost.addEventListener("click", onClick)
  dockviewHost.addEventListener("drop", onDrop)

  let tryAttachFromDomRafId: number | null = null
  const overflowObserver = new MutationObserver(() => {
    if (tryAttachFromDomRafId !== null) return
    tryAttachFromDomRafId = requestAnimationFrame(() => {
      tryAttachFromDomRafId = null
      tryAttach()
    })
  })
  overflowObserver.observe(dockviewHost, { childList: true, subtree: true })

  tryAttach()

  return () => {
    if (tryAttachFromDomRafId !== null) {
      cancelAnimationFrame(tryAttachFromDomRafId)
    }
    overflowObserver.disconnect()
    dockviewHost.removeEventListener("click", onClick)
    dockviewHost.removeEventListener("drop", onDrop)
  }
}

export function useOverflowPanelDrag(api: DockviewApi | null): { dockviewHostRef: Ref<HTMLDivElement> } {
  const [dockviewHostElement, setDockviewHostElement] = useState<HTMLDivElement | null>(null)

  const dockviewHostRef = useCallback((element: HTMLDivElement | null) => {
    setDockviewHostElement(element)
  }, [])

  useEffect(() => {
    if (!api || !dockviewHostElement) return
    return subscribeOverflowPanelDrag(api, dockviewHostElement)
  }, [api, dockviewHostElement])

  return { dockviewHostRef }
}
